import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import AnimatedNumber from "@/components/ui-kit/AnimatedNumber";
import { trackCalculator } from "@/lib/analytics";

const AGENCY_RATE = 0.0025; // 0.25 percent to the referring agency

const usd0 = (n) => `$${Math.max(0, Math.round(n)).toLocaleString("en-US")}`;

// Estimates monthly and annual agency commission.
// clients x average monthly routed volume per client x 0.25 percent.
export default function CommissionCalculator() {
  const [clients, setClients] = useState(25);
  const [volume, setVolume] = useState(1500);
  const [touched, setTouched] = useState(false);

  const { monthly, annual } = useMemo(() => {
    const safeClients = Math.max(0, clients);
    const safeVolume = Math.max(0, volume);
    const monthlyAmt = safeClients * safeVolume * AGENCY_RATE;
    return { monthly: monthlyAmt, annual: monthlyAmt * 12 };
  }, [clients, volume]);

  const onUse = () => {
    if (!touched) {
      setTouched(true);
      trackCalculator("agency-commission");
    }
  };

  return (
    <div className="rounded-3xl border border-etap-deep-100 bg-white p-6 shadow-etap-lg sm:p-8" data-testid="commission-calculator">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="comm-clients" className="flex items-center justify-between text-sm font-semibold text-etap-deep-700">
            Number of clients
            <span className="font-display text-base text-etap-deep-800">{clients}</span>
          </label>
          <Slider
            id="comm-clients"
            data-testid="comm-clients-slider"
            aria-label="Number of clients"
            className="mt-4 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-etap-primary [&_[role=slider]]:bg-white [&_.bg-primary]:bg-etap-primary [&_.bg-primary\\/20]:bg-etap-primary-100"
            min={0}
            max={500}
            step={1}
            value={[clients]}
            onValueChange={(v) => {
              setClients(v[0]);
              onUse();
            }}
          />
          <p className="mt-2 text-xs text-etap-deep-400">0 to 500 clients</p>
        </div>

        <div>
          <label htmlFor="comm-volume" className="flex items-center justify-between text-sm font-semibold text-etap-deep-700">
            Avg monthly routed per client
            <span className="font-display text-base text-etap-deep-800">{usd0(volume)}</span>
          </label>
          <Slider
            id="comm-volume"
            data-testid="comm-volume-slider"
            aria-label="Average monthly routed volume per client"
            className="mt-4 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-etap-primary [&_[role=slider]]:bg-white [&_.bg-primary]:bg-etap-primary [&_.bg-primary\\/20]:bg-etap-primary-100"
            min={0}
            max={10000}
            step={50}
            value={[volume]}
            onValueChange={(v) => {
              setVolume(v[0]);
              onUse();
            }}
          />
          <p className="mt-2 text-xs text-etap-deep-400">The amount each client routes to taxes monthly</p>
        </div>
      </div>

      <div className="mt-7 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-etap-mint-300 bg-etap-mint-100 p-5" data-testid="comm-monthly">
          <p className="text-xs font-semibold uppercase tracking-wider text-etap-deep-600">Estimated monthly commission</p>
          <AnimatedNumber value={monthly} formatter={usd0} className="mt-1 block font-display text-3xl font-bold text-etap-deep-800" />
        </div>
        <div className="rounded-2xl border border-etap-deep-100 bg-etap-deep-50 p-5" data-testid="comm-annual">
          <p className="text-xs font-semibold uppercase tracking-wider text-etap-deep-600">Estimated annual commission</p>
          <AnimatedNumber value={annual} formatter={usd0} className="mt-1 block font-display text-3xl font-bold text-etap-deep-800" />
        </div>
      </div>

      <p className="mt-5 text-xs text-etap-deep-500">
        Based on 0.25 percent to the referring agency. Estimates only, actual earnings depend on real routed volume.
      </p>
    </div>
  );
}
